import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Form, Input } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { ISignProps } from "@models/home";
import ChangeLoginMode from "@pages/home/login/ChangeLoginMode";
import report from "@report";
import useInvalidShake from "@src/shared/resetpassword/useUnvalidShake";
import { updateSubmitLoading } from "@store/globalSlice";
import { emailRule } from "@utils/validators";

import FormCaptchaItem from "../Form/CaptchaItem";
import CommonButton from "@components/CommonButton";

export interface EmailRef {
  setEmail: Function;
  email: {
    validateStatus: "error" | "success" | "warning" | "validating";
    errorMsg: string;
  };
  getFieldsValue: Function;
  resetFields: Function;
}

const EmailWithCode = forwardRef<
  EmailRef,
  {
    type: "login" | "signup" | "verify" | "addSubAccount" | "bingWxAccount";
    submit?: Function;
    checkISAgreeProtocol?: Function;
    preconditionFn?: Function | null | undefined;
  }
>(({ type, submit, checkISAgreeProtocol, preconditionFn }, ref) => {
  const { t } = useTranslation();
  const captchaRef = useRef<any>(null);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { submitLoading } = useAppSelector((state) => state.globalReducer);
  const [emailStatus, setEmailStatus] = useState<{
    validateStatus: "error" | "success" | "warning" | "validating";
    errorMsg: string;
  }>({
    validateStatus: "success",
    errorMsg: "",
  });
  const [email, setEmail] = useState<string>(localStorage.getItem("email") || "");
  const { shakeClassName, handleInvalidShake: _handleInvalidShake } = useInvalidShake();

  const validateEmail = (value: string, isSubmit?: boolean) => {
    if (!value && isSubmit !== true) return;
    localStorage.setItem("email", form.getFieldValue("email"));
    if (value && new RegExp(emailRule)?.test(value)) {
      setEmailStatus({
        validateStatus: "success",
        errorMsg: "",
      });
      return true;
    } else {
      setEmailStatus({
        validateStatus: "error",
        errorMsg: t("Enter a valid Email address (e.g. john@example.com)"),
      });
      return false;
    }
  };

  const onFinish = async (value: ISignProps) => {
    let res = validateEmail(form.getFieldValue("email"), true);
    if (!res) return;
    // 验证码为空时，禁止提交
    const captcha = form.getFieldValue("captcha");
    if (!captcha || !captcha.trim()) {
      captchaRef.current?.validateCaptchaError(t("Please enter verification code"));
      return;
    }
    dispatch(updateSubmitLoading(true));

    // 提交时先校验验证码正确提交 result
    if (validateEmail(form.getFieldValue("email"))) {
      const nextFn = () =>
        submit?.(
          {
            ...value,
            smsType: "email",
          },
          "EMAIL_" + type,
          (err: string) => {
            dispatch(updateSubmitLoading(false));
            // 提交报错
            captchaRef.current.validateCaptchaError(err);
            // 执行验证码错误
            report.event("code_check_fail", {
              kalo_value: form.getFieldValue("email"),
              kalo_type: "email_" + type,
            });
          },
        );
      if (checkISAgreeProtocol && !checkISAgreeProtocol?.(nextFn)) return dispatch(updateSubmitLoading(false));
      nextFn();
    }
  };

  useEffect(() => {
    const captcha_email = sessionStorage.getItem("captcha_email") || "";
    captcha_email && form.setFieldValue("email", captcha_email);
    if (form.getFieldValue("email")) {
      validateEmail(form.getFieldValue("email"));
    }
  }, []);

  useImperativeHandle(ref, () => ({
    setEmail: setEmailStatus,
    email: emailStatus,
    captchaRef: captchaRef.current,
    getFieldsValue: form.getFieldsValue,
    resetFields: form.resetFields,
  }));

  return (
    <div>
      <div className="Component-EmailCode">
        <div className="Component-Form" key="email-code">
          <Form
            form={form}
            initialValues={{ email, captcha: "" }}
            name="register"
            onFinish={onFinish}
            scrollToFirstError
          >
            <Form.Item validateStatus={emailStatus.validateStatus} help={t(emailStatus.errorMsg)} name="email">
              <Input
                placeholder={t("Email")}
                className={twMerge("h-[50px]! text-[14px]! rounded-[4px]!", shakeClassName)}
                onChange={(values) => {
                  const email = values.target.value || "";
                  validateEmail(email);
                  setEmail(email);
                }}
                onFocus={() => {
                  report.event("input", {
                    kalo_pos: "email-input-focus",
                    kalo_value: form.getFieldValue("email"),
                    kalo_type: type,
                  });
                }}
                autoFocus
                autoComplete="email"
              />
            </Form.Item>
            <FormCaptchaItem
              ref={captchaRef}
              {...({ phone: emailStatus } as any)}
              form={form}
              preconditionFn={preconditionFn}
              type={type === "bingWxAccount" ? "signup" : type}
              codeType="email"
              setCaptchaValue={(_value: any) => {}}
            />
            {/* 登录保持不变 */}
            {(type === "login" || type === "signup") && (
              <Form.Item>
                <CommonButton
                  loading={submitLoading}
                  className="w-full h-[50px] text-[16px] rounded-[4px]! mt-[40px]"
                  type="primary"
                >
                  <Trans>{type === "login" ? "Log in" : "Sign up"}</Trans>
                </CommonButton>
              </Form.Item>
            )}
            {type === "bingWxAccount" && (
              <Form.Item>
                <CommonButton
                  loading={submitLoading}
                  className="w-full h-[50px] text-[16px] rounded-[4px]! mt-[40px]"
                  type="primary"
                >
                  <Trans>Confirm</Trans>
                </CommonButton>
              </Form.Item>
            )}
          </Form>
        </div>
      </div>
      {type === "login" && (
        <div className="flex flex-row items-center justify-between">
          <ChangeLoginMode />
          {/* <ForgetPassword
            type="email"
            email={email}
            handleValid={() => {
              const valid = Boolean(validateEmail(email, true));
              handleInvalidShake(valid);
              return valid;
            }}
          ></ForgetPassword> */}
        </div>
      )}
    </div>
  );
});

export default EmailWithCode;
